import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, MapPin, User as UserIcon, Info, ChevronRight } from "lucide-react";
import { formatLocalDate, appointmentTimeLabel } from "@/lib/customerFormat";

/**
 * CUST-EXP-001A1 · NextVisitCard — appointment summary that mirrors
 * the /appointments detail contract: date on its own line, arrival
 * window on its own line, no unrelated creation/update timestamp.
 * If the installer supplied an arrival window we surface that only.
 */

function useCountdown(iso) {
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        if (!iso) return undefined;
        const t = setInterval(() => setNow(new Date()), 60 * 1000);
        return () => clearInterval(t);
    }, [iso]);
    if (!iso) return null;
    const target = new Date(iso).getTime();
    if (Number.isNaN(target)) return null;
    const delta = target - now.getTime();
    if (delta <= 0) return "Happening now";
    const mins = Math.round(delta / 60000);
    if (mins < 60)  return `in ${mins} min${mins === 1 ? "" : "s"}`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `in ${hours} hour${hours === 1 ? "" : "s"}`;
    const days = Math.round(hours / 24);
    if (days < 14)  return `in ${days} day${days === 1 ? "" : "s"}`;
    return `in ${Math.round(days / 7)} weeks`;
}

export default function NextVisitCard({ appt, lifecycle = "active" }) {
    const countdown = useCountdown(appt?.scheduled_at);

    if (!appt) {
        const commissioned = lifecycle === "commissioned";
        return (
            <div className="pe-card p-5 md:p-6" data-testid="next-visit-empty">
                <div className="flex items-start gap-3">
                    <span className="pe-icon-tile w-10 h-10"><Calendar size={16} strokeWidth={1.75} /></span>
                    <div className="flex-1 min-w-0">
                        <div className="text-[11px] tracking-[0.14em] uppercase text-[#4B5565]">Next Visit</div>
                        <div className="mt-1 text-base text-[#131B2F] font-medium">No visits scheduled</div>
                        <p className="mt-1 text-sm text-[#4B5565]" data-testid="next-visit-empty-copy">
                            {commissioned
                                ? "No further visits are currently scheduled. Contact Pearl Energy if you need support."
                                : "Your installer will book the next visit as soon as it's ready. You'll see it here."}
                        </p>
                    </div>
                    <Link
                        to="/appointments"
                        className="hidden md:inline-flex items-center gap-1 text-sm text-[#3E9E2E] font-medium hover:underline shrink-0"
                        data-testid="next-visit-view-empty"
                    >
                        Timeline <ChevronRight size={14} />
                    </Link>
                </div>
            </div>
        );
    }

    const dateLabel = formatLocalDate(appt.scheduled_at);
    const timeLabel = appointmentTimeLabel(appt);

    return (
        <div className="pe-card p-5 md:p-6" data-testid="next-visit-card">
            <div className="flex items-start gap-3">
                <span className="pe-icon-tile w-10 h-10"><Calendar size={16} strokeWidth={1.75} /></span>
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                        <div className="text-[11px] tracking-[0.14em] uppercase text-[#4B5565]">Next Visit</div>
                        {countdown && (
                            <span
                                className="pe-chip !bg-[#F1F8EC] !border-transparent !text-[#2E7E22] font-medium"
                                data-testid="next-visit-countdown"
                            >
                                {countdown}
                            </span>
                        )}
                    </div>
                    <div className="mt-1 text-lg text-[#131B2F] font-medium truncate" data-testid="next-visit-title">
                        {appt.title || appt.appointment_type || "Site visit"}
                    </div>

                    {/* Date line — always its own line. */}
                    {dateLabel && (
                        <div className="mt-2 flex items-center gap-1.5 text-sm text-[#131B2F]" data-testid="next-visit-date">
                            <Clock size={13} strokeWidth={1.75} className="text-[#4B5565]" />
                            {dateLabel}
                        </div>
                    )}
                    {/* Time band — arrival window OR local time only. Never both. */}
                    {timeLabel && (
                        <div className="mt-1 flex items-center gap-1.5 text-sm text-[#4B5565]" data-testid="next-visit-time">
                            <Clock size={13} strokeWidth={1.75} /> {timeLabel}
                        </div>
                    )}

                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#4B5565]">
                        {appt.engineer_name && (
                            <span className="inline-flex items-center gap-1.5" data-testid="next-visit-engineer">
                                <UserIcon size={13} strokeWidth={1.75} /> {appt.engineer_name}
                            </span>
                        )}
                        {appt.location && (
                            <span className="inline-flex items-center gap-1.5">
                                <MapPin size={13} strokeWidth={1.75} /> {appt.location}
                            </span>
                        )}
                    </div>

                    {appt.customer_instructions && (
                        <div className="mt-3 flex items-start gap-2 text-sm text-[#131B2F] bg-[#F5F5F7] border border-[#E5E7EB] rounded-md p-3" data-testid="next-visit-prep">
                            <Info size={13} strokeWidth={1.75} className="mt-0.5 text-[#3E9E2E] shrink-0" />
                            <span>{appt.customer_instructions}</span>
                        </div>
                    )}
                </div>
                <Link
                    to="/appointments"
                    data-testid="next-visit-view"
                    className="hidden md:inline-flex items-center gap-1 text-sm text-[#3E9E2E] hover:text-[#2E7E22] font-medium shrink-0"
                >
                    Details <ChevronRight size={14} />
                </Link>
            </div>
        </div>
    );
}
